import React from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useNavigation } from '@react-navigation/native';
import { MaintenanceDateLabel } from './MaintenanceDateLabel';
import { TicketTypes } from './MaintenanceEmptyState';
import { useMobileColors } from '@/constants/residents/mobile/color';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import {
  getStatusColor,
  getStatusLabel,
  getCategoryName,
} from '@/utils/residents/maintenance';

interface MaintenanceCardProps {
  ticket: IMaintenanceListResponse;
}

export const MaintenanceCard: React.FC<MaintenanceCardProps> = ({ ticket }) => {
  const { colors, semantic } = useThemeColors();
  const DESKTOP_COLORS = useDesktopColors();
  const MOBILE_COLORS = useMobileColors();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const navigation = useNavigation<any>();

  // Use utilities for status info
  const statusBackgroundColor = getStatusColor(ticket);
  const statusText = getStatusLabel(ticket);

  if (!isDesktop) {
    // Mobile view - simpler card layout
    const CardComponent: any = TouchableOpacity;
    const cardProps: any = { onPress: () => navigation?.navigate?.('MaintenanceDetail', { ticket }) };
    return (
      <CardComponent style={[styles.mobileCard, { backgroundColor: semantic.background.secondary, shadowColor: semantic.text.primary }]} {...cardProps}>
        <View style={styles.mobileHeader}>
          <View style={[styles.statusBadge, { backgroundColor: statusBackgroundColor }]}>
            <Text style={[styles.statusText, { color: MOBILE_COLORS.TEXT_SECONDARY }]}>{statusText}</Text>
          </View>
          <MaintenanceDateLabel
            ticketType={ticket.completedOn ? TicketTypes.SOLVED : TicketTypes.OPEN}
            createdOn={ticket.createdOn}
            updatedOn={ticket.updatedOn}
            completedOn={ticket.completedOn}
          />
        </View>
        <Text style={[styles.mobileTitle, { color: colors.text.primary }]}>Ticket #{ticket.maintenanceRequestId}</Text>
        <Text style={[styles.mobileDescription, { color: colors.text.secondary }]} numberOfLines={3}>
          {ticket.maintenanceDescription}
        </Text>
      </CardComponent>
    );
  }

  // Desktop view - community resources card style
  return (
    <View style={[styles.desktopCard, { backgroundColor: DESKTOP_COLORS.PRIMARY_BACKGROUND }]}>
      {/* Status badge row */}
      <View style={styles.statusRow}>
        <View style={[styles.statusBadge, { backgroundColor: statusBackgroundColor }]}>
          <Text style={[styles.statusText, { color: MOBILE_COLORS.TEXT_SECONDARY }]}>{statusText}</Text>
        </View>
        <MaintenanceDateLabel
          ticketType={ticket.completedOn ? TicketTypes.SOLVED : TicketTypes.OPEN}
          createdOn={ticket.createdOn}
          updatedOn={ticket.updatedOn}
          completedOn={ticket.completedOn}
        />
      </View>

      {/* Title and date row */}
      <View style={styles.titleRow}>
        <Text style={[styles.requestTitle, { color: DESKTOP_COLORS.TEXT_PRIMARY }]} numberOfLines={1}>
          Request #{ticket.maintenanceRequestId}
        </Text>
        {/* Intentionally omit right-side date to match web; date is shown in pill above */}
      </View>

      {/* Details grid */}
      <View style={styles.detailsGrid}>
        <View style={styles.detailRow}>
          <RESIDENTS_SVG_COMPONENTS.LOCATION width={14} height={14} color={DESKTOP_COLORS.ICON_INACTIVE} />
          <Text style={[styles.detailText, { color: DESKTOP_COLORS.TEXT_PRIMARY }]} numberOfLines={1}>{ticket.maintenanceLocation}</Text>
        </View>

        <View style={styles.detailRow}>
          <RESIDENTS_SVG_COMPONENTS.WRENCH width={14} height={14} color={DESKTOP_COLORS.ICON_INACTIVE} />
          <Text style={[styles.detailText, { color: DESKTOP_COLORS.TEXT_PRIMARY }]} numberOfLines={1}>
            {getCategoryName(ticket, 'General')}
          </Text>
        </View>

        <View style={styles.detailRow}>
          <RESIDENTS_SVG_COMPONENTS.SPEED width={14} height={14} color={DESKTOP_COLORS.ICON_INACTIVE} />
          <Text style={[styles.detailText, { color: DESKTOP_COLORS.TEXT_PRIMARY }]} numberOfLines={1}>{ticket.maintenancePriority}</Text>
        </View>
      </View>

      {/* Description */}
      <Text style={[styles.descriptionText, { color: DESKTOP_COLORS.TEXT_TERTIARY, borderTopColor: semantic.border.secondary }]} numberOfLines={3}>
        {ticket.maintenanceDescription}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  // Mobile styles
  mobileCard: {
    borderRadius: 12,
    padding: 16,
    marginVertical: 8,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  mobileHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  mobileDate: {
    fontSize: 12,
  },
  mobileTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  mobileDescription: {
    fontSize: 14,
    lineHeight: 20,
  },

  // Desktop styles - matching resio-web BaseCard
  desktopCard: {
    borderRadius: 16, // matches resio-web rounded-2xl
    padding: 24, // matches resio-web p-6 (24px)
    marginBottom: 16,
    // Ensure the card is tall enough to display all sections
    minHeight: 220,
    width: '100%',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8, // matches resio-web space-y-2
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2, // matches resio-web py-0.5
    borderRadius: 12,
    marginRight: 8,
  },
  statusText: {
    fontSize: 12, // matches resio-web text-xs
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12, // matches resio-web mb-3
  },
  requestTitle: {
    fontSize: 16,
    fontWeight: '600', // matches resio-web font-semibold
    flex: 1,
  },
  dateText: {
    fontSize: 12,
    marginLeft: 8,
  },
  detailsGrid: {
    gap: 4, // matches resio-web mb-1 spacing
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailText: {
    fontSize: 14, // matches resio-web text-sm
    flex: 1,
  },
  descriptionText: {
    fontSize: 12, // matches resio-web text-xs
    lineHeight: 18,
    marginTop: 12, // add some separation like resio-web border-t spacing
    paddingTop: 12, // matches the divider spacing in resio-web
    borderTopWidth: 1,
  },
});
